// ============================================================================
// expr.ts — mini-évaluateur arithmétique générique. Récursif-descendant, SANS
// eval ni librairie externe. Supporte : + - * / ^ (et « ** »), parenthèses,
// moins unaire (appliqué après la puissance : -x^2 = -(x²)), variables nommées
// (portée), et les fonctions sin/cos/tan/sqrt/abs/min/max/exp/log (= ln)/sign.
// Le préfixe JavaScript « Math. » est toléré (Math.sqrt(x) = sqrt(x)). Les
// angles trigonométriques sont en RADIANS.
//
// Extrait de packages/ui/.../constrained-geometry/expr.ts pour être partagé
// par tout module pur (côté UI comme côté moteur de génération) sans faire
// dépendre packages/db de packages/ui. `packages/ui` réexporte ce module.
// ============================================================================

type Token =
  | { kind: 'num'; value: number }
  | { kind: 'ident'; value: string }
  | { kind: 'op'; value: string }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'comma' };

const FUNCTIONS: Record<string, (...args: number[]) => number> = {
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  sqrt: Math.sqrt,
  abs: Math.abs,
  min: Math.min,
  max: Math.max,
  exp: Math.exp,
  log: Math.log,
  ln: Math.log,
  sign: Math.sign,
};

const CONSTANTS: Record<string, number> = {
  pi: Math.PI,
  e: Math.E,
};

/** Propriété PROPRE de l'objet (pas `Object.hasOwn` : Safari iOS < 15.4). */
function hasOwn(obj: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Portée d'évaluation : nom de variable → valeur. Les identifiants sont
 * normalisés en MINUSCULES par le tokenizer, donc les clés doivent l'être aussi.
 */
export type Scope = Record<string, number>;

/** Rétro-compat : un nombre est interprété comme la valeur de la variable `x`. */
function toScope(x: number | Scope): Scope {
  return typeof x === 'number' ? { x } : x;
}

/**
 * Construit une vue d'un `Scope` aux clés MINUSCULES, pour un appelant dont
 * les noms de variables peuvent contenir des majuscules (ex. camelCase, comme
 * les noms de paramètres/résultats du Blueprint paramétrique — `BlueprintNameSchema`
 * l'autorise explicitement). Sans ça, `evalExpr("yIntercept", scope)` échoue
 * avec "Identifiant inconnu" car le tokenizer normalise l'identifiant en
 * `yintercept` avant de le chercher dans `scope`, qui garde lui la casse
 * d'origine. `evalExpr` reste volontairement insensible à la casse (contrat
 * documenté sur `Scope` ci-dessus) plutôt que la grammaire — ce helper est le
 * seul point où un appelant doit se plier à ce contrat.
 */
export function toEvalScope(scope: Scope): Scope {
  const lower: Scope = {};
  for (const key of Object.keys(scope)) lower[key.toLowerCase()] = scope[key]!;
  return lower;
}

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === ' ' || c === '\t' || c === '\n') {
      i++;
      continue;
    }
    if (c >= '0' && c <= '9') {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j]!)) j++;
      const value = Number(src.slice(i, j));
      if (Number.isNaN(value)) throw new Error(`Nombre invalide près de "${src.slice(i, j)}"`);
      tokens.push({ kind: 'num', value });
      i = j;
      continue;
    }
    if (/[a-zA-Z_]/.test(c)) {
      let j = i;
      while (j < src.length && /[a-zA-Z0-9_]/.test(src[j]!)) j++;
      const word = src.slice(i, j).toLowerCase();
      // Tolère l'écriture JavaScript « Math.sqrt(x) », « Math.PI » : le préfixe est ignoré.
      if (word === 'math' && src[j] === '.') {
        i = j + 1;
        continue;
      }
      tokens.push({ kind: 'ident', value: word });
      i = j;
      continue;
    }
    // « ** » (puissance JavaScript) = « ^ ».
    if (c === '*' && src[i + 1] === '*') {
      tokens.push({ kind: 'op', value: '^' });
      i += 2;
      continue;
    }
    if ('+-*/^'.includes(c)) {
      tokens.push({ kind: 'op', value: c });
      i++;
      continue;
    }
    if (c === '(') {
      tokens.push({ kind: 'lparen' });
      i++;
      continue;
    }
    if (c === ')') {
      tokens.push({ kind: 'rparen' });
      i++;
      continue;
    }
    if (c === ',') {
      tokens.push({ kind: 'comma' });
      i++;
      continue;
    }
    throw new Error(`Caractère inattendu "${c}" dans l'expression`);
  }
  return tokens;
}

/** Parser récursif-descendant : expr → term (('+'|'-') term)* etc. */
class Parser {
  private pos = 0;
  constructor(
    private tokens: Token[],
    private scope: Scope,
  ) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }
  private next(): Token | undefined {
    return this.tokens[this.pos++];
  }

  parse(): number {
    const v = this.parseExpr();
    if (this.pos !== this.tokens.length) throw new Error('Expression mal formée (tokens restants)');
    return v;
  }

  private parseExpr(): number {
    let v = this.parseTerm();
    let t = this.peek();
    while (t && t.kind === 'op' && (t.value === '+' || t.value === '-')) {
      this.next();
      const rhs = this.parseTerm();
      v = t.value === '+' ? v + rhs : v - rhs;
      t = this.peek();
    }
    return v;
  }

  private parseTerm(): number {
    let v = this.parseUnary();
    let t = this.peek();
    while (t && t.kind === 'op' && (t.value === '*' || t.value === '/')) {
      this.next();
      const rhs = this.parseUnary();
      v = t.value === '*' ? v * rhs : v / rhs;
      t = this.peek();
    }
    return v;
  }

  /**
   * Signe : unary → ('+'|'-') unary | power. Le moins unaire s'applique APRÈS la
   * puissance, comme en maths : « -x^2 » = -(x²), « -2^2 » = -4.
   */
  private parseUnary(): number {
    const t = this.peek();
    if (t && t.kind === 'op' && (t.value === '+' || t.value === '-')) {
      this.next();
      const v = this.parseUnary();
      return t.value === '-' ? -v : v;
    }
    return this.parsePower();
  }

  /** Puissance, associative à droite : power → primary ('^' unary)? (« 2^-1 », « 2^3^2 »). */
  private parsePower(): number {
    const base = this.parsePrimary();
    const t = this.peek();
    if (t && t.kind === 'op' && t.value === '^') {
      this.next();
      return Math.pow(base, this.parseUnary());
    }
    return base;
  }

  private parsePrimary(): number {
    const t = this.next();
    if (!t) throw new Error('Expression incomplète');
    if (t.kind === 'num') return t.value;
    if (t.kind === 'lparen') {
      const v = this.parseExpr();
      const close = this.next();
      if (!close || close.kind !== 'rparen') throw new Error('Parenthèse fermante manquante');
      return v;
    }
    if (t.kind === 'ident') {
      // Appel de fonction ?
      if (this.peek()?.kind === 'lparen') {
        // `hasOwn` : jamais une clé héritée (« constructor », « toString »…)
        const fn = hasOwn(FUNCTIONS, t.value) ? FUNCTIONS[t.value] : undefined;
        if (!fn) throw new Error(`Fonction inconnue : ${t.value}`);
        this.next(); // consomme '('
        const args: number[] = [];
        if (this.peek()?.kind !== 'rparen') {
          args.push(this.parseExpr());
          while (this.peek()?.kind === 'comma') {
            this.next();
            args.push(this.parseExpr());
          }
        }
        const close = this.next();
        if (!close || close.kind !== 'rparen') throw new Error('Parenthèse fermante manquante');
        return fn(...args);
      }
      if (hasOwn(this.scope, t.value)) return this.scope[t.value]!;
      if (hasOwn(CONSTANTS, t.value)) return CONSTANTS[t.value]!;
      throw new Error(`Identifiant inconnu : ${t.value}`);
    }
    throw new Error('Jeton inattendu dans l’expression');
  }
}

/**
 * Évalue une expression arithmétique dans une portée (variables). Rétro-compat :
 * un nombre est interprété comme la valeur de `x`. Lève si l'expression est invalide.
 */
export function evalExpr(src: string, scope: number | Scope): number {
  return new Parser(tokenize(src), toScope(scope)).parse();
}
